/**
 * Payment runs.
 *
 * ═══════════════════════════════════════════════════════════════════
 * THE LAST CONTROL BEFORE MONEY LEAVES.
 *
 * A run is PROPOSED by one person and RELEASED by another. The server refuses a
 * release by the person who prepared it, whatever this screen shows. A supplier
 * with a bank detail change awaiting verification is excluded from the run
 * entirely, and the reason is stated rather than the supplier quietly vanishing.
 *
 * The BACS file is generated once, hashed, and the hash recorded — so the file
 * submitted to the bank can be proved to be the file that was approved.
 * ═══════════════════════════════════════════════════════════════════
 *
 * The run statuses are the server's, not this screen's invention: a run is
 * created `proposed`, becomes `pending_approval` when it is submitted,
 * `approved` when a second person releases it, `file_generated` once the BACS
 * file has been produced and `settled` once the settlement journal is posted.
 * Every button below is gated on the status the server will actually accept, so
 * a control is never offered that the server would refuse.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTenant, useTerms, lower } from '../lib/tenant';
import { date, dateTime, money, number, statusLabel, statusTone, todayIso, addDaysIso } from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  DefinitionList,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  Select,
  TextInput,
} from '../components/ui';

/* ------------------------------------------------------------------ *
 * Payment vocabulary
 * ------------------------------------------------------------------ */

/**
 * `statusLabel` humanises a machine value, which is right for a status but
 * wrong for the payment schemes: it turns `bacs_standard18` into
 * "Bacs standard18" and `bacs` into "Bacs". These are proper names in UK
 * banking and are spelled the way the bank spells them.
 */
const PAYMENT_TERMS: Record<string, string> = {
  bacs: 'BACS',
  bacs_standard18: 'BACS Standard 18',
  csv: 'CSV',
  cheque: 'Cheque',
  chaps: 'CHAPS',
  faster_payment: 'Faster Payment',
};

function paymentTerm(value: string | null | undefined): string {
  if (!value) return '—';
  return PAYMENT_TERMS[value] ?? statusLabel(value);
}

/** A UK sort code is written in three pairs — 20-30-45, never 203045. */
function sortCode(value: string | null | undefined): string {
  if (!value) return '—';
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 6) return value;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
}

/* ------------------------------------------------------------------ *
 * Payment run list
 * ------------------------------------------------------------------ */

interface BatchRow {
  id: string;
  batch_no: string;
  payment_date: string;
  status: string;
  payment_method: string;
  item_count: number | string;
  total_minor: number | string;
  bank_account_name: string | null;
  prepared_by_name: string | null;
  approved_by_name: string | null;
  file_generated_at: string | null;
}

export function PaymentsPage() {
  const { can } = useSession();
  const t = useTerms();
  const list = useAsync(() => api.get<{ batches: BatchRow[] }>('/payables/payment-runs'), []);
  const [proposing, setProposing] = useState(false);

  return (
    <>
      <PageHeader
        title="Payment runs"
        subtitle="Prepared by one person, released by another, and never paying a supplier whose bank details are unverified."
        actions={
          can('ap.payment.prepare') ? (
            <button type="button" className="btn btn--primary" onClick={() => setProposing(true)}>
              Propose a payment run
            </button>
          ) : null
        }
      />

      <Card title="Runs" flush>
        <AsyncPanel state={list}>
          {(data) => (
            <DataTable
              caption={`Payment runs in this ${lower(t.entity)}`}
              rows={data.batches}
              rowKey={(b) => b.id}
              emptyMessage="No payment runs have been prepared yet."
              columns={[
                {
                  key: 'no',
                  header: 'Run',
                  width: '11rem',
                  render: (b) => (
                    <Link to={`/payments/${b.id}`}>
                      <span className="mono">{b.batch_no}</span>
                    </Link>
                  ),
                },
                { key: 'date', header: 'Payment date', render: (b) => date(b.payment_date) },
                { key: 'method', header: 'Method', render: (b) => paymentTerm(b.payment_method) },
                { key: 'count', header: 'Payments', numeric: true, render: (b) => number(Number(b.item_count)) },
                { key: 'total', header: 'Total', numeric: true, render: (b) => <Money minor={Number(b.total_minor)} /> },
                {
                  key: 'people',
                  header: 'Prepared / released',
                  render: (b) => (
                    <span className="smaller">
                      {b.prepared_by_name ?? '—'}
                      <br />
                      <span className="muted">{b.approved_by_name ?? 'Not yet released'}</span>
                    </span>
                  ),
                },
                { key: 'status', header: 'Status', render: (b) => <Badge tone={statusTone(b.status)}>{statusLabel(b.status)}</Badge> },
              ]}
            />
          )}
        </AsyncPanel>
      </Card>

      <ProposeRunModal open={proposing} onClose={() => setProposing(false)} onCreated={() => list.reload()} />
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Proposing a run
 * ------------------------------------------------------------------ */

/**
 * One supplier's place in a proposed run, exactly as the server returns it.
 *
 * Every eligible supplier comes back in `payments`, including the ones that
 * must NOT be paid: those carry an `exclusionReason` and are separated out
 * below rather than being paid by accident. `totalMinor` is the server's own
 * total over the included suppliers only.
 */
interface ProposedPayment {
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  payeeName: string;
  sortCode: string | null;
  accountNumberMasked: string | null;
  invoices: Array<{ invoiceNo: string; dueDate: string; outstandingMinor: number; discountMinor: number }>;
  grossMinor: number;
  discountMinor: number;
  amountMinor: number;
  exclusionReason: string | null;
  warnings: string[];
}

interface ProposalResult {
  payments: ProposedPayment[];
  totalMinor: number;
  excludedCount: number;
}

interface BankAccountOption {
  id: string;
  code: string;
  name: string;
  bank_name: string;
}

function ProposeRunModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const navigate = useNavigate();
  const t = useTerms();
  const [dueOnOrBefore, setDueOnOrBefore] = useState(addDaysIso(todayIso(), 7));
  const [paymentDate, setPaymentDate] = useState(addDaysIso(todayIso(), 3));
  const [bankAccountId, setBankAccountId] = useState('');
  const [proposal, setProposal] = useState<ProposalResult | null>(null);

  // Only fetched while the dialog is open, so opening the Payments screen does
  // not pull the bank accounts for somebody who never proposes a run.
  const banks = useAsync<{ accounts: BankAccountOption[] }>(
    () =>
      open ? api.get<{ accounts: BankAccountOption[] }>('/receivables/bank/accounts') : Promise.resolve({ accounts: [] }),
    [open],
  );

  const criteria = { dueOnOrBefore, paymentDate, bankAccountId, paymentMethod: 'bacs' as const };

  const propose = useMutation(async () => api.post<ProposalResult>('/payables/payment-runs/propose', criteria));

  // The create route takes the criteria nested, and re-runs the selection
  // server-side, so what is created is always what the server itself judged
  // eligible — never a list this screen assembled.
  const create = useMutation(async () =>
    api.post<{ batchId: string; batchNo: string; itemCount: number; totalMinor: number }>('/payables/payment-runs', {
      criteria,
    }),
  );

  const close = () => {
    setProposal(null);
    onClose();
  };

  const included = proposal ? proposal.payments.filter((p) => !p.exclusionReason) : [];
  const excluded = proposal ? proposal.payments.filter((p) => p.exclusionReason) : [];
  const warnings = included.flatMap((p) => p.warnings);

  return (
    <Modal
      open={open}
      onClose={close}
      wide
      title="Propose a payment run"
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={propose.busy || !bankAccountId}
            onClick={() => void propose.run().then((r) => r && setProposal(r))}
          >
            {propose.busy ? 'Checking…' : 'Preview what would be paid'}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={create.busy || !proposal || included.length === 0}
            title={proposal && included.length === 0 ? 'Nothing is eligible for payment with these criteria.' : undefined}
            onClick={() =>
              void create.run().then((r) => {
                if (r) {
                  onCreated();
                  close();
                  // Land on the run itself: the next step is to submit it for
                  // release, and that lives on the detail screen.
                  navigate(`/payments/${r.batchId}`);
                }
              })
            }
          >
            {create.busy ? 'Creating…' : 'Create the run'}
          </button>
        </>
      }
    >
      <div className="stack">
        {/*
          Every criterion clears the preview as it changes. Without that the
          presenter could narrow the dates, still see the old table, and create
          a run paying a different set of suppliers from the one everybody in
          the room just looked at.
        */}
        <div className="grid grid--3">
          <TextInput
            label="Pay invoices due on or before"
            type="date"
            value={dueOnOrBefore}
            onChange={(e) => {
              setDueOnOrBefore(e.currentTarget.value);
              setProposal(null);
            }}
          />
          <TextInput
            label="Payment date"
            type="date"
            min={todayIso()}
            value={paymentDate}
            onChange={(e) => {
              setPaymentDate(e.currentTarget.value);
              setProposal(null);
            }}
          />
          <AsyncPanel state={banks}>
            {(data) => (
              <Select
                label="Pay from"
                value={bankAccountId}
                onChange={(e) => {
                  setBankAccountId(e.currentTarget.value);
                  setProposal(null);
                }}
                required
              >
                <option value="">Choose…</option>
                {data.accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </Select>
            )}
          </AsyncPanel>
        </div>

        <ErrorNotice error={propose.error ?? create.error} />

        {proposal ? (
          <>
            {excluded.length > 0 ? (
              <Alert
                tone="warn"
                title={`${excluded.length} ${excluded.length === 1 ? `${lower(t.supplier)} is` : `${lower(t.supplierPlural)} are`} held back from this run`}
              >
                <ul className="smaller">
                  {excluded.map((p) => (
                    <li key={p.supplierId}>
                      {/* Most of the server's reasons already open with the
                          supplier's name; printing it twice reads as a stutter. */}
                      {p.exclusionReason?.startsWith(p.supplierName)
                        ? p.exclusionReason
                        : `${p.supplierName} — ${p.exclusionReason ?? ''}`}{' '}
                      <span className="muted">{money(p.amountMinor)} not paid.</span>
                    </li>
                  ))}
                </ul>
              </Alert>
            ) : null}

            {warnings.length > 0 ? (
              <Alert tone="info" title="Worth knowing before this run is created">
                <ul className="smaller">
                  {warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </Alert>
            ) : null}

            <DataTable
              caption="Payments that would be made"
              rows={included}
              rowKey={(p) => p.supplierId}
              emptyMessage="Nothing is eligible for payment with these criteria."
              columns={[
                {
                  key: 'supplier',
                  header: t.supplier,
                  render: (p) => (
                    <>
                      {p.supplierName}
                      {p.payeeName !== p.supplierName ? (
                        <div className="smaller muted">Paid to {p.payeeName}</div>
                      ) : null}
                    </>
                  ),
                },
                {
                  key: 'bank',
                  header: 'Bank details',
                  width: '9rem',
                  // Sort code over account number, the way a bank mandate is
                  // written, rather than one long string that wraps wherever
                  // the column happens to run out.
                  render: (p) => (
                    <div className="mono smaller">
                      {sortCode(p.sortCode)}
                      {p.accountNumberMasked ? <div className="muted">{p.accountNumberMasked}</div> : null}
                    </div>
                  ),
                },
                { key: 'count', header: 'Invoices', numeric: true, render: (p) => number(p.invoices.length) },
                { key: 'amount', header: 'Amount', numeric: true, render: (p) => <Money minor={p.amountMinor} /> },
              ]}
              footer={
                <tr className="is-total">
                  <td colSpan={3}>Total</td>
                  {/* The server's own total over the included suppliers — never a
                      sum taken over the rows on screen, which would quietly
                      include anybody held back. */}
                  <td className="num">{money(proposal.totalMinor)}</td>
                </tr>
              }
            />
          </>
        ) : (
          <p className="muted smaller">
            Preview first. The proposal shows exactly who would be paid and, just as importantly, who would be excluded
            and why.
          </p>
        )}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ *
 * Payment run detail
 * ------------------------------------------------------------------ */

interface PaymentRow {
  id: string;
  payment_reference: string;
  payee_name: string;
  supplier_id: string | null;
  amount_minor: number | string;
  status: string;
  sort_code: string | null;
  account_number_masked: string | null;
  invoice_count: number | string;
}

interface BatchDetail {
  batch: {
    id: string;
    batch_no: string;
    status: string;
    payment_date: string;
    bacs_processing_date: string | null;
    payment_method: string;
    total_minor: number | string;
    item_count: number;
    bank_account_name: string | null;
    prepared_by: string | null;
    prepared_by_name: string | null;
    approved_by_name: string | null;
    approved_at: string | null;
    file_generated_at: string | null;
    file_hash: string | null;
    file_format: string | null;
    journal_id: string | null;
    journal_no: string | null;
  };
  payments: PaymentRow[];
}

interface PaymentFileResult {
  filename: string;
  lineCount: number;
  hash: string;
}

/**
 * Where a run has got to, in words.
 *
 * Shown while no payment file exists yet, so the panel beside the control card
 * is never empty and a reader who has not seen the product before can tell what
 * has to happen next without being told out loud.
 */
const RUN_PROGRESS: Record<string, string> = {
  proposed:
    'The run has been prepared but not yet handed on. Submitting it for release puts it in front of somebody who did not prepare it.',
  pending_approval:
    'The run is waiting to be released. Only somebody other than the person who prepared it can release it — the server refuses anybody else, whatever this screen offers.',
  approved:
    'The run has been released by a second person. The payment file is produced next; it is hashed as it is written and the hash is kept against the run.',
  cancelled: 'The run was cancelled. Nothing in it has been paid.',
  rejected: 'The run was refused at release. Nothing in it has been paid.',
};

/**
 * Save text the server produced as a file.
 *
 * `api.download` issues a GET, and the payment-file route is a POST: the file
 * is never stored, only its hash is, so it is rebuilt from the payment records
 * on each request. The response is therefore turned into a download here.
 */
function saveTextFile(content: string, filename: string, mimeType: string) {
  const url = URL.createObjectURL(new Blob([content], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function PaymentRunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can, user } = useSession();
  const { isDemonstration, environmentLabel } = useTenant();
  const t = useTerms();
  const detail = useAsync(() => api.get<BatchDetail>(`/payables/payment-runs/${id}`), [id]);

  const [outcome, setOutcome] = useState<string | null>(null);
  const [remittanceFor, setRemittanceFor] = useState<PaymentRow | null>(null);

  const submit = useMutation(async () =>
    api.post<{ batchNo: string; approverName: string | null }>(`/payables/payment-runs/${id}/submit`, {}),
  );
  const approve = useMutation(async () =>
    api.post<{ batchNo: string; totalMinor: number }>(`/payables/payment-runs/${id}/approve`, {}),
  );
  const generate = useMutation(async () =>
    api.post<PaymentFileResult>(`/payables/payment-runs/${id}/file`, { format: 'bacs_standard18' }),
  );
  const post = useMutation(async () =>
    api.post<{ batchNo: string; journalNo: string; totalMinor: number; invoicesSettled: number }>(
      `/payables/payment-runs/${id}/post`,
      {},
    ),
  );
  const downloadFile = useMutation(async (batchNo: string, format: 'bacs_standard18' | 'csv', processingDate: string) => {
    const content = await api.post<string>(`/payables/payment-runs/${id}/file?download=true`, { format });
    saveTextFile(
      content,
      `${batchNo.replace(/\//g, '-')}-${processingDate.slice(0, 10)}.${format === 'csv' ? 'csv' : 'txt'}`,
      format === 'csv' ? 'text/csv;charset=utf-8' : 'text/plain;charset=utf-8',
    );
  });

  /**
   * Run an action and say plainly on screen what it did.
   *
   * Each of these steps changes who holds the run and what can happen to it
   * next. The server returns that answer; leaving it unsaid is how a presenter
   * ends up clicking Release and having nothing on screen change.
   */
  const act = (perform: () => Promise<string | null>) => {
    setOutcome(null);
    void perform().then((message) => {
      if (message === null) return;
      setOutcome(message);
      detail.reload();
    });
  };

  return (
    <AsyncPanel state={detail}>
      {(data) => {
        const b = data.batch;
        // Compared on the user id, never the display name: two people can share
        // a name, and the server's own refusal is on the id.
        const preparedByMe = Boolean(b.prepared_by) && b.prepared_by === user?.id;
        const awaitingRelease = b.status === 'proposed' || b.status === 'pending_approval';
        const fileFormat: 'bacs_standard18' | 'csv' = b.file_format === 'csv' ? 'csv' : 'bacs_standard18';

        // Each control is offered only in the state the server will accept it
        // in, so nothing on this screen can be pressed and refused.
        const canSubmit = b.status === 'proposed' && can('ap.payment.prepare');
        const canRelease = awaitingRelease && can('ap.payment.approve');
        const canGenerate = b.status === 'approved' && can('ap.payment.prepare');
        const canPost =
          (b.status === 'file_generated' || b.status === 'submitted') && !b.journal_id && can('ap.payment.approve');

        return (
          <>
            <PageHeader
              title={`Payment run ${b.batch_no}`}
              subtitle={`${b.item_count} payment${b.item_count === 1 ? '' : 's'} · ${money(Number(b.total_minor))}`}
              meta={
                <>
                  <Badge tone={statusTone(b.status)}>{statusLabel(b.status)}</Badge>
                  <span>Payment date {date(b.payment_date)}</span>
                  {b.bank_account_name ? <span>From {b.bank_account_name}</span> : null}
                  {b.journal_id ? (
                    <Link to={`/ledger/journals/${b.journal_id}`}>
                      {b.journal_no ? `View journal ${b.journal_no}` : 'View the journal'}
                    </Link>
                  ) : null}
                </>
              }
              actions={
                canSubmit || canRelease || canGenerate || canPost ? (
                  <div className="btn-group">
                    {canSubmit ? (
                      <button
                        type="button"
                        className="btn"
                        disabled={submit.busy}
                        onClick={() =>
                          act(async () => {
                            const r = await submit.run();
                            if (!r) return null;
                            return r.approverName
                              ? `Run ${r.batchNo} has been submitted for release. It is now with ${r.approverName}.`
                              : `Run ${r.batchNo} has been submitted for release.`;
                          })
                        }
                      >
                        {submit.busy ? 'Submitting…' : 'Submit for release'}
                      </button>
                    ) : null}

                    {canRelease ? (
                      <button
                        type="button"
                        className="btn btn--primary"
                        disabled={approve.busy || preparedByMe}
                        title={preparedByMe ? 'You prepared this run, so you cannot release it.' : undefined}
                        onClick={() =>
                          act(async () => {
                            const r = await approve.run();
                            return (
                              r &&
                              `Run ${r.batchNo} released for payment: ${money(r.totalMinor)}. Generate the payment file next.`
                            );
                          })
                        }
                      >
                        {approve.busy ? 'Releasing…' : 'Release the run'}
                      </button>
                    ) : null}

                    {canGenerate ? (
                      <button
                        type="button"
                        className="btn"
                        disabled={generate.busy}
                        onClick={() =>
                          act(async () => {
                            const r = await generate.run();
                            return (
                              r && `Payment file ${r.filename} produced: ${r.lineCount} lines, hashed and recorded against the run.`
                            );
                          })
                        }
                      >
                        {generate.busy ? 'Generating…' : 'Generate the BACS file'}
                      </button>
                    ) : null}

                    {canPost ? (
                      <button
                        type="button"
                        className="btn btn--primary"
                        disabled={post.busy}
                        onClick={() =>
                          act(async () => {
                            const r = await post.run();
                            return (
                              r &&
                              `Settlement posted as journal ${r.journalNo}: ${money(r.totalMinor)} across ${r.invoicesSettled} invoice${r.invoicesSettled === 1 ? '' : 's'}.`
                            );
                          })
                        }
                      >
                        {post.busy ? 'Posting…' : 'Post the settlement'}
                      </button>
                    ) : null}
                  </div>
                ) : undefined
              }
            />

            <ErrorNotice
              error={submit.error ?? approve.error ?? generate.error ?? post.error ?? downloadFile.error}
            />

            {outcome ? (
              <Alert tone="ok" title="Done">
                <p>{outcome}</p>
              </Alert>
            ) : null}

            {preparedByMe && awaitingRelease ? (
              <Alert tone="info" title="You prepared this run">
                <p>
                  You cannot release your own payment run. Somebody holding the payment approval permission has to
                  release it — the two-person control on money leaving the {lower(t.organisation)}.
                </p>
              </Alert>
            ) : null}

            <div className="stack">
              <Card title="Payments" flush>
                <DataTable
                  caption={`Payments in run ${b.batch_no}`}
                  rows={data.payments}
                  rowKey={(p) => p.id}
                  emptyMessage="This run holds no payments."
                  columns={[
                    {
                      key: 'ref',
                      header: 'Reference',
                      width: '15rem',
                      render: (p) => <span className="mono smaller">{p.payment_reference}</span>,
                    },
                    {
                      key: 'payee',
                      header: 'Payee',
                      render: (p) =>
                        p.supplier_id && can('supplier.view') ? (
                          <Link to={`/suppliers/${p.supplier_id}`}>{p.payee_name}</Link>
                        ) : (
                          p.payee_name
                        ),
                    },
                    {
                      key: 'bank',
                      header: 'Bank details',
                      width: '9rem',
                      // Sort code over account number, the way a bank mandate is
                      // written. This is the pair a Finance Director checks
                      // hardest before releasing money, so it is laid out to be
                      // read rather than run together.
                      render: (p) => (
                        <div className="mono smaller">
                          {sortCode(p.sort_code)}
                          {p.account_number_masked ? <div className="muted">{p.account_number_masked}</div> : null}
                        </div>
                      ),
                    },
                    {
                      key: 'invoices',
                      header: 'Invoices',
                      numeric: true,
                      render: (p) => number(Number(p.invoice_count)),
                    },
                    { key: 'amount', header: 'Amount', numeric: true, render: (p) => <Money minor={Number(p.amount_minor)} /> },
                    { key: 'status', header: 'Status', render: (p) => <Badge tone={statusTone(p.status)}>{statusLabel(p.status)}</Badge> },
                    {
                      key: 'remit',
                      header: <span className="visually-hidden">Remittance</span>,
                      render: (p) => (
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setRemittanceFor(p)}>
                          Remittance
                        </button>
                      ),
                    },
                  ]}
                  footer={
                    <tr className="is-total">
                      <td colSpan={4}>Total</td>
                      <td className="num">{money(Number(b.total_minor))}</td>
                      <td colSpan={2} />
                    </tr>
                  }
                />
              </Card>

              <div className="grid grid--2">
                <Card title="Control">
                  <DefinitionList
                    items={[
                      ['Prepared by', b.prepared_by_name ?? '—'],
                      [
                        'Released by',
                        b.approved_by_name ? (
                          <>
                            {b.approved_by_name}
                            <div className="smaller muted">{b.approved_at ? dateTime(b.approved_at) : ''}</div>
                          </>
                        ) : (
                          'Not yet released'
                        ),
                      ],
                      ['Method', paymentTerm(b.payment_method)],
                      ['BACS processing date', b.bacs_processing_date ? date(b.bacs_processing_date) : '—'],
                    ]}
                  />
                  {b.prepared_by_name && b.approved_by_name && b.prepared_by_name !== b.approved_by_name ? (
                    <p className="smaller muted" style={{ marginTop: '0.75rem' }}>
                      Prepared and released by different people.
                    </p>
                  ) : null}
                </Card>

                {b.file_generated_at ? (
                  <Card title="The payment file">
                    <DefinitionList
                      items={[
                        ['Format', paymentTerm(b.file_format)],
                        ['Generated', dateTime(b.file_generated_at)],
                      ]}
                    />
                    <p className="field__label" style={{ marginTop: '0.75rem' }}>
                      SHA-256 of the file
                    </p>
                    <p className="mono smaller" style={{ wordBreak: 'break-all' }}>
                      {b.file_hash ?? '—'}
                    </p>
                    <p className="smaller muted">
                      The hash is recorded when the file is generated, so the file handed to the bank can be proved to
                      be the file that was released.
                    </p>
                    {/*
                      Said plainly rather than left to be assumed. Nothing is
                      transmitted from here to any bank, and an IT reviewer is
                      entitled to know that without having to ask.
                    */}
                    {isDemonstration ? (
                      <p className="smaller muted">
                        The file is produced in the shape a BACS bureau expects. In this{' '}
                        {environmentLabel.toLowerCase()} it is generated and hashed but never transmitted — a live
                        deployment hands it to your own Bacstel-IP submission software.
                      </p>
                    ) : (
                      <p className="smaller muted">
                        Altari produces and hashes the file. Submission to the bank is done by your own Bacstel-IP
                        software.
                      </p>
                    )}

                    {b.status === 'file_generated' && can('ap.payment.prepare') ? (
                      <button
                        type="button"
                        className="btn btn--sm"
                        style={{ marginTop: '0.75rem' }}
                        disabled={downloadFile.busy}
                        onClick={() =>
                          void downloadFile.run(
                            b.batch_no,
                            fileFormat,
                            b.bacs_processing_date ?? b.payment_date,
                          )
                        }
                      >
                        {downloadFile.busy ? 'Preparing…' : 'Download the file'}
                      </button>
                    ) : b.status === 'settled' || b.status === 'part_settled' ? (
                      <p className="smaller muted" style={{ marginTop: '0.75rem' }}>
                        The run has settled, so the file is not produced again. The hash above is the record of what
                        was released.
                      </p>
                    ) : null}
                  </Card>
                ) : RUN_PROGRESS[b.status] ? (
                  <Card title="Where this run has got to">
                    <p className="smaller">{RUN_PROGRESS[b.status]}</p>
                  </Card>
                ) : null}
              </div>
            </div>

            <RemittanceModal payment={remittanceFor} onClose={() => setRemittanceFor(null)} />
          </>
        );
      }}
    </AsyncPanel>
  );
}

/* ------------------------------------------------------------------ *
 * Remittance advice
 * ------------------------------------------------------------------ */

interface Remittance {
  payeeName: string;
  paymentReference: string;
  paymentDate: string;
  amountMinor: number;
  lines: Array<{
    invoiceNo: string;
    supplierReference: string;
    invoiceDate: string;
    grossMinor: number;
    discountMinor: number;
    paidMinor: number;
  }>;
}

/**
 * The remittance advice, on screen.
 *
 * This is the answer to "what is that ten thousand pounds?", so it is shown as
 * a document a finance reader recognises rather than handed over as a file.
 */
function RemittanceModal({ payment, onClose }: { payment: PaymentRow | null; onClose: () => void }) {
  return (
    <Modal
      open={payment !== null}
      onClose={onClose}
      wide
      title={payment ? `Remittance advice — ${payment.payee_name}` : 'Remittance advice'}
      footer={
        <button type="button" className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      {/* Mounted only while a payment is chosen, so the advice is fetched fresh
          each time one is opened rather than held from the last. */}
      {payment ? <RemittanceBody paymentId={payment.id} /> : null}
    </Modal>
  );
}

function RemittanceBody({ paymentId }: { paymentId: string }) {
  const remittance = useAsync(() => api.get<Remittance>(`/payables/payments/${paymentId}/remittance`), [paymentId]);

  return (
    <AsyncPanel state={remittance}>
      {(data) => (
        <div className="stack">
          <DefinitionList
            items={[
              ['Paid to', data.payeeName],
              ['Payment reference', <span className="mono smaller">{data.paymentReference}</span>],
              ['Payment date', date(data.paymentDate)],
              ['Amount', <Money minor={data.amountMinor} bold />],
            ]}
          />
          <DataTable
            caption={`Invoices settled by ${data.paymentReference}`}
            rows={data.lines}
            rowKey={(l) => l.invoiceNo}
            emptyMessage="No invoices are allocated to this payment."
            columns={[
              { key: 'invoice', header: 'Invoice', render: (l) => <span className="mono smaller">{l.invoiceNo}</span> },
              { key: 'ref', header: 'Their reference', render: (l) => l.supplierReference },
              { key: 'date', header: 'Invoice date', render: (l) => date(l.invoiceDate) },
              { key: 'gross', header: 'Gross', numeric: true, render: (l) => <Money minor={l.grossMinor} /> },
              {
                key: 'discount',
                header: 'Discount',
                numeric: true,
                render: (l) => <Money minor={l.discountMinor} blankIfZero />,
              },
              { key: 'paid', header: 'Paid', numeric: true, render: (l) => <Money minor={l.paidMinor} /> },
            ]}
            footer={
              <tr className="is-total">
                <td colSpan={5}>Total paid</td>
                <td className="num">{money(data.amountMinor)}</td>
              </tr>
            }
          />
        </div>
      )}
    </AsyncPanel>
  );
}
